import { useEffect, useState } from 'react';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { onFirstInteraction } from '@/lib/interaction';

export type SceneTier = 'full' | 'lite' | 'none';

/** Cheap one-off capability probe; the context is released immediately. */
function hasWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

/**
 * The one place every GL scene reads its tier from — v7's mistake was letting
 * each scene (`GlyphField`, `Building`) re-derive its own pointer/width/
 * reduced-motion checks, which is how they drifted (Work's gallery used a
 * different breakpoint than the hero). One hook, one set of rules.
 *
 * `full`   — desktop, fine pointer, capable hardware: every GL scene renders
 *            at full fidelity (curl noise, physical materials).
 * `lite`   — anything that fails one full-tier check but still has WebGL:
 *            scenes fall back to their 2D canvas / static renderer.
 * `none`   — no WebGL, or `prefers-reduced-motion`: no canvas mounts at all.
 *
 * Deliberately not derived until first interaction — the WebGL probe
 * allocates a context, which is real cost that must never sit on the load
 * path (`CLAUDE.md` §10).
 */
export function useSceneTier(): SceneTier {
  const reduced = useReducedMotion();
  const large = useMediaQuery('(min-width: 1024px)');
  const finePointer = useMediaQuery('(pointer: fine)');
  const [webgl, setWebgl] = useState<boolean | null>(null);

  useEffect(() => {
    if (reduced) return;
    // Gated behind first interaction, not a bare timeout: allocating a GL
    // context is real work, and CLAUDE.md §10 requires nothing WebGL-related
    // to run before a visitor has actually done something. Every consumer
    // already renders its `lite` placeholder for however long this takes.
    return onFirstInteraction(() => setWebgl(hasWebGL()));
  }, [reduced]);

  if (reduced) return 'none';
  if (webgl === false) return 'none';
  if (webgl === null) return 'lite';
  return large && finePointer ? 'full' : 'lite';
}

/** Hardware floor for the heaviest scene (the hero field) — `full` tier only. */
export function hasCapableHardware(): boolean {
  const nav = navigator as Navigator & {
    deviceMemory?: number;
    hardwareConcurrency?: number;
  };
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory < 4) return false;
  if (typeof nav.hardwareConcurrency === 'number' && nav.hardwareConcurrency < 4) return false;
  return true;
}
